import { get, post, patch, deleteRequest, deleteWithPayload, isDev } from "../../../shared/data";
import { CUBISCAN_DEFAULT_UOM } from "./constants";
import { capitalizeFirstLetter } from "../../../shared/helpers";

export const getPositions = () => {
    return get(`/measuring/positions`).then((data) => {
        return data.map((p) => {
            return {
                id: p.id,
                text: p.name,
                value: p.code,
                attributeGroup: p.attributeGroup,
            };
        });
    });
};

export const getMeasurementData = (shipmentKey) => {
    return get(`/measuring/wips/measurements/multiple/${shipmentKey}`).then((data) => {
        return data.map((c) => {
            return {
                upc: c.metaData.upc,
                productModifier: c.metaData.modifier,
                measuringWipKey: c.metaData.measuringWipKey,
                taskContextKey: c.metaData.taskContextKey,
                measurements: c.measurements,
            };
        });
    });
};

export const getProductMeasurementTaskInfo = (params, isInDuplicateStaging) => {
    const isDuplicate = isInDuplicateStaging ? "?showDuplicateStagedProducts=true" : "";

    return post(`/tasking/tasks/measuring.standard/search${isDuplicate}`, { taskContextKeys: params });
};

const buildWipData = (value) => {
    return {
        data: value,
        toolvalue: "",
    };
};

const convertMeasurementsToWip = (payload, wipKeys) => {
    const payLoadContainer = {};

    let items = [];

    wipKeys.forEach((key) => {
        let newPayload = {};

        newPayload.height = buildWipData(payload.height);
        newPayload.width = buildWipData(payload.width);
        newPayload.depth = buildWipData(payload.depth);
        newPayload.weight = buildWipData(payload.weight);

        newPayload.measuringWipKey = key;
        items.push(newPayload);
    });

    payLoadContainer.measurementType = payload.type;
    payLoadContainer.positionCode = payload.positionValue;
    payLoadContainer.Items = items;

    return payLoadContainer;
};

const patchMeasurements = (payload) => {
    const payLoadContainer = {};
    let items = [];
    payload.key.forEach((key) => {
        let newPayload = {};
        newPayload.height = buildWipData(payload.height);
        newPayload.width = buildWipData(payload.width);
        newPayload.depth = buildWipData(payload.depth);
        newPayload.weight = buildWipData(payload.weight);

        newPayload.MeasurementKey = key;
        items.push(newPayload);
    });

    payLoadContainer.Items = items;

    return payLoadContainer;
};

export const validateMeasurementTask = (key) => {
    return post(`/tasking/tasks/${key}/validate?showDuplicateStagedProducts=true`);
};

export const saveMultiMeasurements = async (payload, wipKeys) => {
    const converted = convertMeasurementsToWip(payload, wipKeys);

    try {
        const data = await post(`/measuring/wips/measurements/multiple`, converted);

        if (data.errors && data.errors.length > 0) {
            return data;
        }

        let newData = data.filter((d) => d.measurementType === payload.type && converted.positionCode.toLowerCase() === d.position.code);

        return mapWipMeasurements(newData);
    } catch {
        return false;
    }
};

export const updateWipMeasurements = async (payload, sourcePage) => {
    const converted = patchMeasurements(payload);
    const source = `${sourcePage ? `?sourcePage=${sourcePage}` : ""}`;

    const data = await patch(`/measuring/wips/measurements/multiple${source}`, converted);

    if (data) {
        return true;
    }

    throw new Error("Unable to save");
};

export const getWipMeasurements = (wipKey) => {
    return get(`/measuring/wips/${wipKey}/measurements`).then((data) => {
        return data.map(mapWipMeasurements);
    });
};

export const deleteMeasurements = (keys) => {
    return deleteWithPayload("/measuring/wips/measurements/multiple", keys);
};

export const deleteWipMeasurements = (wipKey, measurementKey) => {
    return deleteRequest(`/measuring/wips/${wipKey}/measurements/${measurementKey}`);
};

const mapWipMeasurements = (c) => {
    const mapWipArrayKeys = () => {
        let keys = [];
        c.forEach((n) => keys.push(n.key));

        c = c[c.length - 1];

        return {
            positionValue: c.position.code,
            position: c.position.name,
            fullPosition: c.position,
            height: c.height.data,
            width: c.width.data,
            weight: c.weight.data,
            depth: c.depth.data,
            key: keys,
            type: c.measurementType || "Imperial",
        };
    };

    const mapSingleWipKey = () => {
        return {
            positionValue: c.position.code,
            position: c.position.name,
            fullPosition: c.position,
            height: c.height.data,
            width: c.width.data,
            weight: c.weight.data,
            depth: c.depth.data,
            key: [c.key],
            type: c.measurementType || "Imperial",
        };
    };

    return c instanceof Array ? mapWipArrayKeys() : mapSingleWipKey();
};

const mapStringsToSelect = (stringArr) => {
    return stringArr.map((string) => {
        return {
            value: string,
            text: string,
        };
    });
};

export const getSenders = () => {
    if (isDev) {
        return Promise.resolve(mapStringsToSelect(["Cubiscan 1"]));
    }

    return get("/measuring/cubiscan/senders").then(mapStringsToSelect);
};

export const requestMeasurements = async (sender) => {
    let cubiscanData;

    if (isDev) {
        cubiscanData = [
            {
                sender: "Virtual Cubiscan",
                receivedOn: "2021-07-21T17:25:26.9677203-05:00",
                height: 1.0,
                width: 2.0,
                depth: 3.0,
                weight: 0,
                key: "PZ48U99A",
                measurementType: "Metric    ",
                dimensionUom: "mm",
                weightUom: "kg",
            },
        ];
    } else {
        cubiscanData = await get(`/measuring/cubiscan/${sender}`);
    }

    if (!cubiscanData || cubiscanData.length === 0) {
        throw new Error("No data sent from cubiscan");
    }

    return cubiscanData.map((d) => {
        const measurementType = d.measurementType ? capitalizeFirstLetter(d.measurementType.trim()) : "Imperial";

        return {
            sender: d.sender,
            receivedOn: d.receivedOn,
            height: { value: d.height, uom: d.dimensionUom || CUBISCAN_DEFAULT_UOM[measurementType].height },
            width: { value: d.width, uom: d.dimensionUom || CUBISCAN_DEFAULT_UOM[measurementType].width },
            depth: { value: d.depth, uom: d.dimensionUom || CUBISCAN_DEFAULT_UOM[measurementType].depth },
            weight: { value: d.weight, uom: d.weightUom || CUBISCAN_DEFAULT_UOM[measurementType].weight },
            key: d.key,
            measurementType: measurementType,
        };
    });
};

export const deleteCubiscanMeasurements = (idArray) => {
    return post(`/measuring/cubiscan/deleted`, { keys: idArray });
};
